// The Consumer API client. See Design > Exchange, "Consumer API". It sends
// only what the consumer built: no test-run or toolset headers, which belong to
// curators and the operator.

import type { AccountResponse, ApiFailure, AskRequest, AskResponse, ExamplesResponse, Json, JsonObject, RejectResult, SessionResponse, ShapesResponse, ThingsResponse } from "./domain/types.ts";

export type Result<T> = { ok: true; value: T } | { ok: false; error: ApiFailure };

export type Api = {
  ask(request: AskRequest): Promise<Result<AskResponse>>;
  reject(answerId: string, reason?: string): Promise<Result<RejectResult>>;
  things(refs: string[]): Promise<Result<ThingsResponse>>;
  /** Shape definitions by pinned shape ref, for field descriptions. */
  shapes(refs: string[]): Promise<Result<ShapesResponse>>;
  /** Called once after sign-in: the WarmHub user, created where sign-up is open, and their account. */
  session(): Promise<Result<SessionResponse>>;
  /** The caller's consumer account: balance and latest ledger entries. */
  account(): Promise<Result<AccountResponse>>;
  /** The hubs the environment lists, with the questions a consumer can try. */
  examples(): Promise<Result<ExamplesResponse>>;
};

/** A bearer token for one request, or null when there is none to send. */
export type TokenProvider = () => Promise<string | null> | string | null;

/**
 * `onRefused` runs when the Exchange refuses the caller rather than the
 * request: a 401, such as an expired or missing token, or a 403
 * NotOperatorOrgMember. The Console then ends the session and says why.
 */
export function createApi(base: string, fetchImpl: typeof fetch = (...a) => fetch(...a), token: TokenProvider = () => null, onRefused: (e: ApiFailure) => void = () => undefined): Api {
  async function call<T>(path: string, init: RequestInit = {}): Promise<Result<T>> {
    const headers = new Headers(init.headers);
    if (init.body) headers.set("content-type", "application/json");
    const bearer = await token();
    if (bearer) headers.set("authorization", `Bearer ${bearer}`);
    let res: Response;
    try {
      res = await fetchImpl(`${base}${path}`, { ...init, headers });
    } catch {
      return { ok: false, error: { _tag: "NetworkError", detail: "The Console couldn't reach the Exchange.", status: 0 } };
    }
    const body: Json = await res.json().catch(() => null);
    if (res.ok) {
      // SAFETY: a 2xx Consumer API body is the type its endpoint declares, and each method below picks T for its own path (Design > Exchange, "Consumer API").
      return { ok: true, value: body as T };
    }
    const b: JsonObject = body !== null && typeof body === "object" && !Array.isArray(body) ? body : {};
    const retryAfter = Number(res.headers.get("retry-after"));
    const error: ApiFailure = {
      _tag: typeof b._tag === "string" ? b._tag : `Http${res.status}`,
      status: res.status,
      ...(typeof b.detail === "string" ? { detail: b.detail } : {}),
      ...(typeof b.reason === "string" ? { reason: b.reason } : {}),
      ...(typeof b.retryable === "boolean" ? { retryable: b.retryable } : {}),
      ...(Number.isFinite(retryAfter) && retryAfter > 0 ? { retryAfter } : {}),
    };
    if (res.status === 401 || error._tag === "NotOperatorOrgMember") onRefused(error);
    return { ok: false, error };
  }
  return {
    ask: (request) => call("/v1/ask", { method: "POST", body: JSON.stringify(request) }),
    reject: (answerId, reason) => call(`/v1/answers/${encodeURIComponent(answerId)}/reject`, { method: "POST", body: JSON.stringify(reason ? { reason } : {}) }),
    things: (refs) => call(`/v1/things?${refs.map((r) => `ref=${encodeURIComponent(r)}`).join("&")}`),
    shapes: (refs) => call(`/v1/shapes?${refs.map((r) => `ref=${encodeURIComponent(r)}`).join("&")}`),
    session: () => call("/v1/session", { method: "POST", body: "{}" }),
    account: () => call("/v1/account"),
    examples: () => call("/v1/examples"),
  };
}

/** A failure in words. */
export function describeFailure(e: ApiFailure): string {
  return (FAILURES.get(e._tag) ?? other)(e);
}

const other = (e: ApiFailure) => e.detail ?? `The Exchange returned ${e.status}${e.retryable ? ". You can try again" : ""}.`;

// The failures that have their own words; the Exchange can name others.
const FAILURES = new Map<string, (e: ApiFailure) => string>([
  ["RateLimited", (e) => `Too many requests. Try again in ${e.retryAfter ?? 60} seconds.`],
  ["Unauthenticated", (e) => e.reason === "NoWarmHubUser" ? "Your WarmHub account has no WarmHub user yet, and sign-up isn't open here. Sign in to WarmHub once, then come back."
    : "You're signed out. Sign in to send a request."],
  ["InvalidRequest", (e) => `The Exchange refused the request: ${e.reason ?? "it isn't valid"}.`],
  ["NetworkError", (e) => e.detail ?? "The Console couldn't reach the Exchange."],
]);
